import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, useStripe, useElements } from '@stripe/react-stripe-js';
import SavedPaymentMethodsSelector from '../checkout/components/SavedPaymentMethodsSelector';
import StripeCardForm from '../checkout/components/StripeCardForm';
import {
  useGetSavedPaymentMethodsQuery,
  useCreateSetupIntentMutation,
  useSavePaymentMethodMutation
} from '../../redux/apis/endpoints/paymentApi';
import {
  useGetCreditPackagesQuery,
  usePurchaseCreditPackageMutation,
  useConfirmCreditPurchaseMutation
} from '../../redux/apis/endpoints/productPhotoApi';
import { BACKEND_URL } from '../../utils/config';
import { useHistory } from 'react-router-dom';

const authHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('token')}`
});

const fmtDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch (e) {
    return '';
  }
};

const STRIPE_KEY = typeof window !== 'undefined' && window._env_
  ? window._env_.REACT_APP_STRIPE_PUBLISHABLE_KEY
  : (typeof process !== 'undefined' && process.env
    ? (process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || process.env.REACT_APP_STRIPE_PUBLIC_KEY)
    : null);

const stripePromise = STRIPE_KEY ? loadStripe(STRIPE_KEY) : null;

const BRAND_TEAL = '#14b8a6';

/**
 * Inner form component that has access to Stripe hooks.
 * IMPORTANT: The CardElement must remain fully visible and mounted during
 * the entire payment flow. Stripe checks element visibility -- display:none
 * or unmounting causes IntegrationError.
 */
const CreditPaymentForm = ({ currentBalance, onSuccess, onClose }) => {
  const stripe = useStripe();
  const elements = useElements();

  const [step, setStep] = useState('select'); // select | payment | success
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [error, setError] = useState(null);
  const [useNewCard, setUseNewCard] = useState(true);
  const [selectedSavedMethod, setSelectedSavedMethod] = useState(null);
  const [showCardElement, setShowCardElement] = useState(false);
  const [cardKey] = useState(Date.now());
  const [grantedCredits, setGrantedCredits] = useState(0);
  const [newBalance, setNewBalance] = useState(0);
  const [saveCard, setSaveCard] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isProcessingNative, setIsProcessingNative] = useState(false);
  const [nativeError, setNativeError] = useState(null);

  const isNativeApp = window.ReactNativeWebView !== undefined;

  const history = useHistory();
  const [subscription, setSubscription] = useState(null);

  // Load subscription status so we can show Active/Manage instead of subscribe
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/credit-packages/subscription-status`, { headers: authHeaders() });
        if (res.ok && active) setSubscription(await res.json());
      } catch (e) { /* non-fatal */ }
    })();
    return () => { active = false; };
  }, []);

  const openManageSubscription = useCallback(() => {
    const params = new URLSearchParams(window.location.search);
    params.set('manage', 'subscription');
    history.replace({ pathname: window.location.pathname, search: `?${params.toString()}` });
    if (onClose) onClose();
  }, [history, onClose]);

  const { data: packagesData } = useGetCreditPackagesQuery();
  const { data: savedPaymentMethods = [] } = useGetSavedPaymentMethodsQuery();
  const [purchaseCredits] = usePurchaseCreditPackageMutation();
  const [confirmPurchase] = useConfirmCreditPurchaseMutation();
  const [createSetupIntent] = useCreateSetupIntentMutation();
  const [savePaymentMethod] = useSavePaymentMethodMutation();

  const packages = packagesData?.packages || [];

  useEffect(() => {
    if (packages.length > 0 && !selectedPackage) {
      const popular = packages.find(p => p.popular);
      setSelectedPackage(popular || packages[0]);
    }
  }, [packages, selectedPackage]);

  // Default-select the saved card (matches the rental checkout). Without this,
  // selectedSavedMethod stays null and savedPaymentMethodId never reaches the
  // backend, so the PaymentIntent gets no card → "no payment method" 400.
  useEffect(() => {
    if (savedPaymentMethods.length > 0) {
      const defaultMethod = savedPaymentMethods.find(m => m.isDefault) || savedPaymentMethods[0];
      setSelectedSavedMethod(defaultMethod);
      setUseNewCard(false);
    } else {
      setUseNewCard(true);
      setSelectedSavedMethod(null);
    }
  }, [savedPaymentMethods]);

  useEffect(() => {
    if (!isNativeApp) return;

    const handleMessage = (event) => {
      let data;
      try {
        data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
      } catch (e) {
        return;
      }
      if (data && data.type === 'NATIVE_PURCHASE_SUCCESS') {
        setGrantedCredits(selectedPackage?.credits || 0);
        setIsProcessingNative(false);
        setStep('success');
        if (onSuccess) onSuccess({
          creditsGranted: selectedPackage?.credits || 0
        });
      } else if (data && data.type === 'NATIVE_PURCHASE_ERROR') {
        setNativeError(data.payload.error);
        setIsProcessingNative(false);
      }
    };

    window.addEventListener('message', handleMessage);
    document.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
      document.removeEventListener('message', handleMessage);
    };
  }, [isNativeApp, selectedPackage, onSuccess]);

  const handleCardReady = useCallback(() => setShowCardElement(true), []);
  const handleCardChange = useCallback((event) => {
    setError(event.error ? event.error.message : null);
  }, []);

  const handleSubscribe = async () => {
    setNativeError(null);
    if (isNativeApp) {
      setIsProcessingNative(true);
      window.ReactNativeWebView.postMessage(JSON.stringify({
        type: 'INITIATE_IAP',
        payload: { productId: `sheyou.subscription.creator_monthly` }
      }));
      return;
    }

    if (!stripe) { setError('Payment system not ready. Please try again.'); return; }
    setIsProcessing(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');

      // 1. Create the subscription on the backend (embedded flow — no redirect)
      const res = await fetch(`${BACKEND_URL}/credit-packages/subscribe-embedded`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          planId: 'creator_monthly',
          savedPaymentMethodId: (!useNewCard && selectedSavedMethod) ? selectedSavedMethod.id : undefined,
          returnUrl: window.location.href
        })
      });
      const data = await res.json();
      if (!data.success || (!data.clientSecret && data.paymentStatus !== 'succeeded')) {
        throw new Error(data.message || 'Failed to create subscription');
      }

      // 2. Confirm payment with Stripe (unless already confirmed by saved card)
      if (data.paymentStatus !== 'succeeded' && data.clientSecret) {
        let confirmResult;
        if (!useNewCard && selectedSavedMethod && selectedSavedMethod.stripePaymentMethodId) {
          confirmResult = await stripe.confirmCardPayment(data.clientSecret, {
            payment_method: selectedSavedMethod.stripePaymentMethodId
          });
        } else if (!useNewCard && selectedSavedMethod) {
          confirmResult = await stripe.confirmCardPayment(data.clientSecret);
        } else {
          const cardElement = elements.getElement('card');
          if (!cardElement) throw new Error('Card form not loaded. Please refresh and try again.');
          confirmResult = await stripe.confirmCardPayment(data.clientSecret, {
            payment_method: { card: cardElement }
          });
        }
        if (confirmResult.error) throw new Error(confirmResult.error.message);
      }

      // 3. Activate subscription + grant credits on backend
      const confirmRes = await fetch(`${BACKEND_URL}/credit-packages/confirm-subscription`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ subscriptionId: data.subscriptionId })
      });
      const confirmData = await confirmRes.json();
      if (!confirmData.success) {
        throw new Error(confirmData.message || 'Subscription activation failed');
      }

      // 4. Show success + refresh subscription status
      setGrantedCredits(120);
      setNewBalance(confirmData.creditBalance || 0);
      setSubscription({ active: true, plan: 'creator' });
      setStep('success');
      if (onSuccess) onSuccess({
        creditsGranted: 120,
        creditBalance: confirmData.creditBalance,
        isSubscription: true
      });
    } catch (err) {
      setError(err.message || 'Failed to start subscription. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSubmit = async () => {
    if (!stripe || !selectedPackage || isProcessing) return;
    setError(null);
    setIsProcessing(true);

    try {
      const purchaseResult = await purchaseCredits({
        packageId: selectedPackage.id,
        savedPaymentMethodId: selectedSavedMethod?.id || undefined
      }).unwrap();

      if (!purchaseResult.success) {
        throw new Error(purchaseResult.message || 'Failed to create payment');
      }

      const { clientSecret, paymentIntentId: piId, paymentStatus } = purchaseResult;
      let stripeResult;

      // When using a saved card, the backend auto-confirms the PaymentIntent.
      // Calling confirmCardPayment again would cause Stripe to return a 400.
      if (paymentStatus === 'succeeded') {
        stripeResult = { paymentIntent: { id: piId, status: 'succeeded' } };
      } else if (selectedSavedMethod && !useNewCard) {
        // Saved card. The backend already attached it to the PaymentIntent, so a
        // plain confirm works (matches the rental checkout). If we have the
        // Stripe payment-method id, pass it explicitly as a belt-and-suspenders.
        stripeResult = selectedSavedMethod.stripePaymentMethodId
          ? await stripe.confirmCardPayment(clientSecret, { payment_method: selectedSavedMethod.stripePaymentMethodId })
          : await stripe.confirmCardPayment(clientSecret);
      } else {
        const cardElement = elements.getElement('card');
        if (!cardElement) throw new Error('Card form not loaded. Please refresh and try again.');
        stripeResult = await stripe.confirmCardPayment(clientSecret, {
          payment_method: { card: cardElement }
        });
      }

      if (stripeResult.error) throw new Error(stripeResult.error.message);

      // Save card if user checked the option
      if (saveCard && useNewCard && stripeResult.paymentIntent?.status === 'succeeded') {
        try {
          const setupResult = await createSetupIntent().unwrap();
          const cardEl = elements.getElement('card');
          if (cardEl) {
            const confirmSetup = await stripe.confirmCardSetup(setupResult.clientSecret, {
              payment_method: { card: cardEl }
            });
            if (!confirmSetup.error) {
              await savePaymentMethod({
                setupIntentId: confirmSetup.setupIntent.id,
                isDefault: savedPaymentMethods.length === 0
              }).unwrap();
            }
          }
        } catch (saveErr) {
          console.warn('Failed to save card:', saveErr);
        }
      }

      const confirmResult = await confirmPurchase({
        paymentIntentId: stripeResult.paymentIntent.id
      }).unwrap();

      if (confirmResult.success) {
        setGrantedCredits(confirmResult.creditsGranted || selectedPackage.credits);
        setNewBalance(confirmResult.creditBalance || 0);
        setStep('success');
        if (onSuccess) onSuccess({
          creditsGranted: confirmResult.creditsGranted,
          creditBalance: confirmResult.creditBalance
        });
      } else {
        throw new Error('Credit confirmation failed');
      }
    } catch (err) {
      setError(err.message || 'Payment failed. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  if (step === 'success') {
    return (
      <div style={{ textAlign: 'center', padding: '24px 0' }}>
        <div style={{ width: 48, height: 48, margin: '0 auto 12px', color: '#059669' }}>
          <svg viewBox="0 0 48 48" fill="none">
            <circle cx="24" cy="24" r="22" stroke="currentColor" strokeWidth="3" />
            <path d="M14 24l7 7 13-13" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
        <div style={{ fontSize: 18, fontWeight: 600, color: '#111' }}>
          {subscription && subscription.active ? "You're subscribed to Creator!" : `+${grantedCredits} Credits Added`}
        </div>
        <div style={{ fontSize: 13, color: '#6b7280', marginTop: 6 }}>
          {newBalance > 0 ? `Your new balance is ${newBalance} credits.` : `+${grantedCredits} credits added.`}
        </div>
        <button onClick={onClose} style={{ marginTop: 20, padding: '10px 32px', background: BRAND_TEAL, color: '#fff', border: 'none', borderRadius: '9999px', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
          Continue
        </button>
      </div>
    );
  }

  return (
    <>
      {currentBalance !== null && currentBalance !== undefined && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: '#f9fafb', borderRadius: 8, marginBottom: 14 }}>
          <span style={{ fontSize: 13, color: '#6b7280' }}>Current balance</span>
          <span style={{ fontSize: 14, fontWeight: 600, color: currentBalance > 0 ? BRAND_TEAL : '#ef4444' }}>{currentBalance} credits</span>
        </div>
      )}

      {step === 'select' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16, opacity: isProcessing ? 0.5 : 1, pointerEvents: isProcessing ? 'none' : 'auto' }}>
          {packages.map(pkg => {
            const selected = selectedPackage?.id === pkg.id;
            return (
              <div key={pkg.id} onClick={() => setSelectedPackage(pkg)} role="radio" aria-checked={selected} tabIndex={0}
                style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', border: `2px solid ${selected ? BRAND_TEAL : '#e5e7eb'}`, borderRadius: 10, cursor: 'pointer', background: selected ? '#f0fdfa' : '#fff', transition: 'all 0.15s' }}>
                <div style={{ width: 18, height: 18, borderRadius: '50%', border: `2px solid ${selected ? BRAND_TEAL : '#d1d5db'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  {selected && <div style={{ width: 10, height: 10, borderRadius: '50%', background: BRAND_TEAL }} />}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 500, color: '#111', display: 'flex', alignItems: 'center', gap: 6 }}>
                    {pkg.name}
                    {pkg.popular && <span style={{ fontSize: 10, fontWeight: 600, color: BRAND_TEAL, background: '#ccfbf1', padding: '2px 6px', borderRadius: 4, textTransform: 'uppercase' }}>Popular</span>}
                  </div>
                  <div style={{ fontSize: 12, color: '#9ca3af' }}>{pkg.credits} AI generations · ${(pkg.price / pkg.credits).toFixed(2)}/credit</div>
                </div>
                <div style={{ fontSize: 15, fontWeight: 600, color: '#111' }}>${pkg.price.toFixed(2)}</div>
              </div>
            );
          })}
        </div>
      )}

      {step === 'select' && (
        <button onClick={() => {
          if (isNativeApp) {
            setIsProcessingNative(true);
            window.ReactNativeWebView.postMessage(JSON.stringify({
              type: 'INITIATE_IAP',
              payload: { productId: `sheyou.credits.${selectedPackage.id}` }
            }));
          } else {
            setStep('payment');
          }
        }} disabled={!selectedPackage || isProcessingNative}
          style={{ width: '100%', padding: '12px', background: BRAND_TEAL, color: '#fff', border: 'none', borderRadius: '9999px', fontSize: 14, fontWeight: 600, cursor: 'pointer', opacity: selectedPackage && !isProcessingNative ? 1 : 0.5 }}>
          Continue · ${selectedPackage?.price?.toFixed(2) || ''}
        </button>
      )}

      {step === 'select' && (
        <>
          {/* Divider */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '20px 0 16px', color: '#9ca3af', fontSize: 13 }}>
            <div style={{ flex: 1, height: 1, background: '#e5e7eb' }} />
            <span>generate often?</span>
            <div style={{ flex: 1, height: 1, background: '#e5e7eb' }} />
          </div>

          {/* Creator Monthly Subscription */}
          <div style={{ border: '1px solid #e5e7eb', borderRadius: 12, padding: '16px 18px', marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 15, fontWeight: 600, color: '#111' }}>Creator</span>
                <span style={{ fontSize: 11, fontWeight: 500, color: '#6b7280', background: '#f3f4f6', padding: '2px 8px', borderRadius: 4 }}>Monthly</span>
              </div>
              <span style={{ fontSize: 15, fontWeight: 600, color: '#111' }}>$14.99<span style={{ fontSize: 13, fontWeight: 400, color: '#6b7280' }}>/mo</span></span>
            </div>
            <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 14, lineHeight: 1.5 }}>
              120 credits monthly · $0.12/credit
            </div>
            {subscription && subscription.active && subscription.plan === 'creator' ? (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: BRAND_TEAL, background: '#e6f7f6', padding: '2px 10px', borderRadius: 9999 }}>Active</span>
                  {subscription.cancelAtPeriodEnd
                    ? <span style={{ fontSize: 12, color: '#6b7280' }}>Ends {fmtDate(subscription.renewsAt)} · credits kept until then</span>
                    : (subscription.renewsAt && <span style={{ fontSize: 12, color: '#6b7280' }}>Renews {fmtDate(subscription.renewsAt)}</span>)}
                </div>
                <button
                  onClick={openManageSubscription}
                  style={{ width: '100%', padding: '10px', background: 'transparent', color: '#374151', border: '1px solid #d1d5db', borderRadius: '9999px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
                >
                  Manage subscription
                </button>
              </div>
            ) : (
              <>
                <button
                  onClick={() => { setStep('subscribe'); }}
                  disabled={isProcessing || isProcessingNative}
                  style={{ width: '100%', padding: '10px', background: '#fff', color: '#111', border: '1px solid #d1d5db', borderRadius: '9999px', fontSize: 14, fontWeight: 500, cursor: 'pointer', transition: 'all 0.15s' }}
                  onMouseEnter={e => { e.target.style.background = '#f9fafb'; e.target.style.borderColor = BRAND_TEAL; }}
                  onMouseLeave={e => { e.target.style.background = '#fff'; e.target.style.borderColor = '#d1d5db'; }}
                >
                  Go Creator monthly
                </button>
                <div style={{ textAlign: 'center', fontSize: 12, color: '#9ca3af', marginTop: 6 }}>Cancel anytime</div>
              </>
            )}
          </div>

          {nativeError && <div style={{ color: '#ef4444', fontSize: 13, marginBottom: 16, padding: '8px 12px', background: '#fef2f2', borderRadius: 8 }}>{nativeError}</div>}
          {isProcessingNative && (
            <div style={{ padding: '20px 0', color: BRAND_TEAL, fontWeight: 600, fontSize: 14, textAlign: 'center', marginBottom: 16 }}>
              <style>{`
                @keyframes pulse {
                  0% { opacity: 0.5; }
                  50% { opacity: 1; }
                  100% { opacity: 0.5; }
                }
              `}</style>
              <div style={{ animation: 'pulse 1.5s infinite' }}>Processing App Store Payment...</div>
            </div>
          )}

          {/* Invite link */}
          <div style={{ textAlign: 'center', fontSize: 13, color: BRAND_TEAL, cursor: 'pointer' }}
            onClick={() => {
              if (navigator.share) {
                navigator.share({ title: 'Join SHEYOU', text: 'Sign up and we both get +3 credits!', url: window.location.origin });
              } else {
                navigator.clipboard.writeText(window.location.origin);
              }
            }}
          >
            Invite a friend — you both get +3 credits
          </div>
        </>
      )}

      {step === 'payment' && (
        <>
          <hr style={{ border: 'none', borderTop: '1px solid #e5e7eb', margin: '12px 0' }} />
          <div style={{ opacity: isProcessing ? 0.5 : 1, pointerEvents: isProcessing ? 'none' : 'auto' }}>
            <SavedPaymentMethodsSelector savedPaymentMethods={savedPaymentMethods} selectedSavedMethod={selectedSavedMethod}
              useNewCard={useNewCard} onSelectedMethodChange={setSelectedSavedMethod} onUseNewCardChange={setUseNewCard} />
            {useNewCard && (
              <>
                <StripeCardForm cardKey={cardKey} showCardElement={showCardElement || true}
                  onCardChange={handleCardChange} onCardReady={handleCardReady} saveCard={saveCard} />
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', marginBottom: 12 }}>
                  <input type="checkbox" checked={saveCard} onChange={() => setSaveCard(!saveCard)}
                    style={{ width: 16, height: 16, accentColor: BRAND_TEAL, cursor: 'pointer' }} />
                  <span style={{ marginLeft: 8, fontSize: 13, color: '#374151' }}>Save this card for future purchases</span>
                </label>
              </>
            )}
          </div>

          {error && <div style={{ color: '#ef4444', fontSize: 13, marginTop: 8, padding: '8px 12px', background: '#fef2f2', borderRadius: 8 }}>{error}</div>}

          <button onClick={handleSubmit} disabled={isProcessing || !stripe || (useNewCard && !showCardElement)}
            style={{ width: '100%', padding: '12px', background: BRAND_TEAL, color: '#fff', border: 'none', borderRadius: '9999px', fontSize: 14, fontWeight: 600, cursor: 'pointer', marginTop: 12, opacity: (isProcessing || !stripe || (useNewCard && !showCardElement)) ? 0.6 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
            {isProcessing && <span style={{ width: 16, height: 16, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', animation: 'creditSpin 1s linear infinite', display: 'inline-block' }} />}
            {isProcessing ? 'Processing...' : `Pay $${selectedPackage?.price?.toFixed(2)} for ${selectedPackage?.credits} Credits`}
          </button>

          {!isProcessing && (
            <div style={{ textAlign: 'center', marginTop: 8 }}>
              <button type="button" onClick={() => setStep('select')}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: '#6b7280' }}>
                Back to packages
              </button>
            </div>
          )}
        </>
      )}

      {step === 'subscribe' && (
        <>
          <div style={{ padding: '8px 0 12px', fontSize: 14, fontWeight: 600, color: '#111' }}>
            Creator Monthly · $14.99/mo
            <div style={{ fontSize: 12, fontWeight: 400, color: '#6b7280', marginTop: 2 }}>120 credits monthly · cancel anytime</div>
          </div>
          <hr style={{ border: 'none', borderTop: '1px solid #e5e7eb', margin: '4px 0 12px' }} />
          <div style={{ opacity: isProcessing ? 0.5 : 1, pointerEvents: isProcessing ? 'none' : 'auto' }}>
            <SavedPaymentMethodsSelector savedPaymentMethods={savedPaymentMethods} selectedSavedMethod={selectedSavedMethod}
              useNewCard={useNewCard} onSelectedMethodChange={setSelectedSavedMethod} onUseNewCardChange={setUseNewCard} />
            {useNewCard && (
              <StripeCardForm cardKey={cardKey} showCardElement={showCardElement || true}
                onCardChange={handleCardChange} onCardReady={handleCardReady} saveCard={false} />
            )}
          </div>

          {error && <div style={{ color: '#ef4444', fontSize: 13, marginTop: 8, padding: '8px 12px', background: '#fef2f2', borderRadius: 8 }}>{error}</div>}

          <button onClick={handleSubscribe} disabled={isProcessing || !stripe || (useNewCard && !showCardElement)}
            style={{ width: '100%', padding: '12px', background: BRAND_TEAL, color: '#fff', border: 'none', borderRadius: '9999px', fontSize: 14, fontWeight: 600, cursor: 'pointer', marginTop: 12, opacity: (isProcessing || !stripe || (useNewCard && !showCardElement)) ? 0.6 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
            {isProcessing && <span style={{ width: 16, height: 16, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', animation: 'creditSpin 1s linear infinite', display: 'inline-block' }} />}
            {isProcessing ? 'Processing...' : 'Subscribe · $14.99/mo'}
          </button>

          {!isProcessing && (
            <div style={{ textAlign: 'center', marginTop: 8 }}>
              <button type="button" onClick={() => setStep('select')}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: '#6b7280' }}>
                Back to packages
              </button>
            </div>
          )}
        </>
      )}
    </>
  );
};

/**
 * CreditPurchaseModal -- renders its own overlay (not using Modal component)
 * to avoid nested-portal z-index issues with the VTO overlay (z-9990).
 */
const CreditPurchaseModal = ({ isOpen, onClose, currentBalance, onSuccess }) => {
  const overlayRef = useRef(null);

  useEffect(() => {
    if (isOpen) document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleOverlayClick = (e) => {
    if (e.target === overlayRef.current) onClose();
  };

  return ReactDOM.createPortal(
    <>
      <style>{`@keyframes creditSpin { to { transform: rotate(360deg); } }
        @keyframes creditSlideUp { from { transform: translateY(16px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }`}</style>
      <div ref={overlayRef} onClick={handleOverlayClick}
        style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 10000, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
        <div style={{ background: '#fff', borderRadius: 16, maxWidth: 440, width: '100%', maxHeight: '85vh', overflow: 'hidden', boxShadow: '0 25px 50px rgba(0,0,0,0.2)', animation: 'creditSlideUp 0.25s ease', display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: '14px 20px', borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
            <span style={{ fontSize: 15, fontWeight: 600, color: '#111' }}>Get More Credits</span>
            <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: '#9ca3af', lineHeight: 1, padding: '4px' }}>&#x2715;</button>
          </div>
          <div style={{ padding: '20px', overflowY: 'auto', flex: 1 }}>
            {stripePromise ? (
              <Elements stripe={stripePromise}>
                <CreditPaymentForm currentBalance={currentBalance} onSuccess={onSuccess} onClose={onClose} />
              </Elements>
            ) : (
              <div style={{ textAlign: 'center', padding: '24px 0', color: '#6b7280', fontSize: 14 }}>
                Payment system is not configured. Please try again later.
              </div>
            )}
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};

export default CreditPurchaseModal;
